import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Accordion, AccordionSummary, Box, Button, List, ListItem, Skeleton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import random from 'lodash/random';
import { MuiAppBar } from 'lib/appbar/MuiAppBar';
import { ApplicationState } from 'store/store';

const PREFIX = 'MenuSkeleton';

const classes = {
    menuContainer: `${PREFIX}menuContainer`,
    menu: `${PREFIX}menu`,
    listItem: `${PREFIX}listItem`,
    panel: `${PREFIX}panel`,
    viewBasketButton: `${PREFIX}viewBasketButton`,
    expandIcon: `${PREFIX}expandIcon`,
    panelSummary: `${PREFIX}panelSummary`
};

const StyledBox = styled(Box)(({ theme }) => ({
    height: '100%',
    width: '100%',
    [`& .${classes.menuContainer}`]: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: theme.spacing(1, 0),
        paddingBottom: theme.spacing(7),
        overflowY: 'hidden'
    },

    [`& .${classes.menu}`]: {
        flex: 1,
        overflowY: 'auto',
        padding: theme.spacing(0, 1.5)
    },

    [`& .${classes.listItem}`]: {
        padding: theme.spacing(1, 0),
        borderBottom: `1px solid ${theme.palette.divider}`
    },

    [`& .${classes.panel}`]: {
        width: '100%'
    },

    [`& .${classes.viewBasketButton}`]: {
        width: `calc(100% - ${theme.spacing(2)})`,
        padding: theme.spacing(1),
        margin: theme.spacing(0, 1),
        marginTop: 'auto'
    },

    [`& .${classes.expandIcon}`]: {
        padding: theme.spacing(3, 1.5),
        '& svg': {
            fontSize: '32px',
            color: theme.palette.divider
        }
    },

    [`& .${classes.panelSummary}`]: {
        padding: 0,
        alignItems: 'flex-start'
    }
}));

export const MenuSkeleton: React.FC<React.PropsWithChildren> = () => {
    const { t } = useTranslation();
    const { currentLocation } = useSelector((state: ApplicationState) => state.locations);

    return (
        <StyledBox className="hidden-scroll">
            <MuiAppBar title={currentLocation ? currentLocation.title : 'Menu'} />
            <div className={classes.menuContainer}>
                <div className={classes.menuContainer}>
                    <List className={classes.menu}>
                        {Array(7)
                            .fill('')
                            .map((_, index) => (
                                <ListItem key={index} disableGutters className={classes.listItem}>
                                    <Accordion
                                        expanded={false}
                                        className={classes.panel}
                                        elevation={0}
                                        slotProps={{
                                            transition: { unmountOnExit: true, exit: false }
                                        }}
                                    >
                                        <AccordionSummary
                                            classes={{
                                                expandIconWrapper: classes.expandIcon
                                            }}
                                            className={classes.panelSummary}
                                            expandIcon={<ExpandMoreIcon />}
                                        >
                                            <Box width="100%">
                                                <Skeleton height={32} width={random(70, 120)} />
                                                <Skeleton height={20} width={random(90, 140)} />
                                            </Box>
                                        </AccordionSummary>
                                    </Accordion>
                                </ListItem>
                            ))}
                    </List>
                </div>
                <Button color="primary" variant="contained" className={classes.viewBasketButton} disabled>
                    {t('PREORDER_VIEW_BASKET')}
                </Button>
            </div>
        </StyledBox>
    );
};
